/**
 * useGlobalClickSound — глобальный делегат.
 *
 * Регистрирует pointerdown listener на window и при клике на ЛЮБОЙ
 * интерактивный элемент (button, [role=button], a, label, summary)
 * проигрывает `uiTap`.
 *
 * Это гарантия что ВСЕ нажатия в интерфейсе сопровождаются звуком
 * без необходимости править каждый компонент отдельно. Специфические
 * звуки (bellOpen, claimStart, slotCardTap и т.д.) играются поверх
 * через свои click handlers — это нормально и даёт двухслойную
 * аудио-картину (феедбек от тапа + смысл действия).
 *
 * Опт-аут: элементу ИЛИ его предку можно добавить
 * `data-no-sound="true"` — делегат его пропустит. Это позволяет
 * заворачивать целые зоны (например, платёжку) одним атрибутом,
 * чтобы внутри играл только их собственный специфический звук
 * без двойного слоя глобального uiTap.
 */
import { useEffect } from 'react';
import { useUISounds, warmupAllPools } from './useUISounds';

const INTERACTIVE_SELECTOR =
  'button, [role="button"], a[href], label[for], summary, input[type="submit"], input[type="button"]';

export function useGlobalClickSound() {
  const sounds = useUISounds();

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // Только primary кнопка мыши и touch
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      // warmupAllPools теперь делает AudioContext.resume() — это
      // НЕ HTMLAudio.play(), активацию не съедает. Безопасно
      // вызывать перед реальным play() в том же user-gesture.
      warmupAllPools();

      const target = e.target as HTMLElement | null;
      if (!target || !target.closest) return;

      const interactive = target.closest(INTERACTIVE_SELECTOR);
      if (!interactive) return;

      // Опт-аут — сам элемент или любой его предок с
      // data-no-sound="true" отключает глобальный tap
      if ((interactive as HTMLElement).closest('[data-no-sound="true"]')) return;
      // Отключён — disabled кнопки не звучат
      if (
        interactive instanceof HTMLButtonElement &&
        interactive.disabled
      ) return;

      sounds.tap();
    };

    // iOS / Telegram WebView: после того как Mini App свернули и
    // открыли обратно, audio-сессия засыпает. Первый последующий
    // play() беззвучен. Решение — грети́м заново на каждом возврате
    // в foreground И при фокусе окна. Плюс отложенный warmup через
    // requestAnimationFrame — на iOS иногда нужно 2 "тика" чтобы
    // WebView успел восстановить audio-контекст.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        warmupAllPools();
        requestAnimationFrame(() => warmupAllPools());
        window.setTimeout(() => warmupAllPools(), 400);
      }
    };
    const onFocus = () => {
      warmupAllPools();
    };

    window.addEventListener('pointerdown', onDown, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    // Первый warmup на монтировании — гарантия что audio-сессия
    // проснулась к моменту первого клика.
    warmupAllPools();

    return () => {
      window.removeEventListener('pointerdown', onDown, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [sounds]);
}
